/** Typed client for the console API. */

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

const TOKEN_KEY = "rd.console.token";

export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setToken(token: string): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode: the session simply will not persist */
  }
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(path, {
    ...init,
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401) {
    setToken("");
    onUnauthorized?.();
    throw new ApiError(401, "Your session has ended. Sign in again.");
  }

  const text = await res.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? (typeof data === "string" ? data : `Request failed (${res.status})`));
  }
  if (data && typeof data === "object" && "error" in data && data.error) {
    throw new ApiError(res.status, String(data.error));
  }
  return data as T;
}

/**
 * Sends a file as the body, rather than wrapped in JSON.
 *
 * Base64 in a JSON body costs a third of the size again and has to be built
 * in memory first; the metadata rides in the query string instead.
 */
async function upload<T>(path: string, data: Blob): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/octet-stream" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, { method: "POST", headers, body: data });
  if (res.status === 401) {
    setToken("");
    onUnauthorized?.();
    throw new ApiError(401, "Your session has ended. Sign in again.");
  }
  const text = await res.text();
  let parsed: any = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok || parsed?.error) {
    throw new ApiError(res.status, parsed?.error ?? `Upload failed (${res.status})`);
  }
  return parsed as T;
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  upload,
  post: <T>(p: string, b?: unknown) => request<T>("POST", p, b),
  put: <T>(p: string, b?: unknown) => request<T>("PUT", p, b),
  patch: <T>(p: string, b?: unknown) => request<T>("PATCH", p, b),
  del: <T>(p: string, b?: unknown) => request<T>("DELETE", p, b),
};

// ---------------------------------------------------------------- types --

export interface Me {
  id: string;
  name: string;
  display_name: string;
  is_admin: boolean;
  permissions: string[];
  tfa_enabled: boolean;
  group_id: string | null;
  role_id: string | null;
}

export interface Device {
  guid: string;
  id: string;
  online: boolean;
  last_online: number;
  ip: string;
  version: string;
  note: string;
  status: number;
  hostname: string;
  username: string;
  os: string;
  cpu: string;
  memory: string;
  has_pk: boolean;
  user_id: string | null;
  user_name: string;
  device_group_id: string | null;
  device_group_name: string;
  strategy_id: string | null;
  strategy_name: string;
  created_at: number;
}

export interface Paged<T> {
  total: number;
  data: T[];
}

export interface User {
  id: string;
  name: string;
  email: string;
  display_name: string;
  note: string;
  status: number;
  is_admin: boolean;
  role_id: string | null;
  role_name: string;
  group_id: string | null;
  group_name: string;
  tfa_enabled: boolean;
  third_auth_type: string;
  created_at: number;
  last_login_at: number;
}

export interface Named {
  id: string;
  name: string;
  note: string;
  created_at: number;
}

export interface Role extends Named {
  permissions: string[];
  built_in: boolean;
}

export interface AccessRule {
  id: string;
  subject_type: "user" | "group" | "all";
  subject_id: string;
  object_type: "device" | "device_group" | "all";
  object_id: string;
  permissions: string;
  permission_names: string[];
  allow: boolean;
  priority: number;
  note: string;
  created_at: number;
}

export interface Strategy {
  id: string;
  name: string;
  note: string;
  options: Record<string, string>;
  extra: Record<string, string>;
  is_default: boolean;
  modified_at: number;
  targets: Array<{ id: string; target_type: string; target_id: string }>;
}

export interface RelayServer {
  id: string;
  host: string;
  ws_host: string;
  region: string;
  enabled: number;
  weight: number;
  capacity: number;
  sessions: number;
  last_seen: number;
  note: string;
}

export interface AddressBook {
  guid: string;
  name: string;
  owner: string;
  owner_name: string;
  personal: boolean;
  note: string;
  peer_count: number;
  rules: Array<{ id: string; user_id: string | null; group_id: string | null; rule: number }>;
}

export interface Overview {
  devices: number;
  devices_online: number;
  users: number;
  groups: number;
  device_groups: number;
  address_books: number;
  relays: number;
  sessions_24h: number;
  recent: Array<Record<string, any>>;
}

export interface ServerConfig {
  host: string;
  key: string;
  api: string;
  relay: string;
  config_string: string;
  filename_style: string;
  complete: boolean;
}

export interface Release {
  tag: string;
  name: string;
  published_at: string;
  assets: Array<{ name: string; size: number; platform: string; arch: string }>;
}

export interface Script {
  id: string;
  name: string;
  note: string;
  interpreter: string;
  content: string;
  created_by: string | null;
  created_by_name?: string;
  created_at: number;
  updated_at: number;
  size?: number;
}

export interface ScriptTarget {
  id: string;
  guid: string;
  online: boolean;
  hostname: string;
  os: string;
  can_terminal: boolean;
  has_password: boolean;
  /** Why this machine cannot be a target; empty when it can. */
  blocked: string;
}

export interface ScriptRun {
  id: string;
  batch_id: string;
  script_id: string | null;
  script_name: string;
  device_id: string;
  user_id: string | null;
  username: string;
  status: "pending" | "running" | "ok" | "failed" | "refused" | "timeout";
  exit_code: number;
  output: string;
  error: string;
  started_at: number;
  finished_at: number;
}

export interface PendingDevice {
  guid: string;
  id: string;
  ip: string;
  version: string;
  hostname: string;
  username: string;
  os: string;
  first_seen: number;
}

export interface IpRule {
  id: string;
  subject_type: "user" | "all";
  subject_id: string;
  subject_name: string;
  cidr: string;
  allow: boolean;
  note: string;
  created_at: number;
}

export interface ApiTokenRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  created_by: string | null;
  last_used_at: number;
  expires_at: number;
  revoked: boolean;
  created_at: number;
}

// ----------------------------------------------------------------- fleet --

export interface FleetFacts {
  hostname?: string;
  os?: string;
  os_build?: string;
  uptime_hours?: number;
  last_boot?: string;
  cpu?: string;
  memory_gb?: number;
  disks?: Array<{ name: string; total_gb: number; free_gb: number }>;
  pending_reboot?: boolean;
  macs?: string[];
  local_ips?: string[];
  logged_in?: string;
  manufacturer?: string;
  model?: string;
  serial?: string;
  domain?: string;
  [key: string]: unknown;
}

export interface FleetRow {
  id: string;
  name: string;
  os: string;
  version: string;
  online: boolean;
  last_online: number;
  group: string;
  facts: FleetFacts;
  collected_at: number;
  error: string;
}

export interface ScheduleRow {
  id: string;
  name: string;
  script_id: string | null;
  builtin: string;
  target_type: string;
  target_id: string;
  every_minutes: number;
  enabled: number;
  last_run_at: number;
  last_run_batch: string;
  script_name: string;
  target_name: string;
  run_as_name: string;
}

export interface PushBatch {
  batch_id: string;
  filename: string;
  target_dir: string;
  username: string;
  started_at: number;
  total: number;
  done: number;
  failed: number;
}

export interface PushRow {
  id: string;
  batch_id: string;
  device_id: string;
  device_name: string;
  filename: string;
  target_dir: string;
  size: number;
  status: string;
  error: string;
  started_at: number;
  finished_at: number;
}

export interface GrantRow {
  id: string;
  subject_id: string;
  user_name: string;
  object_type: string;
  object_id: string;
  object_name: string;
  permissions: string[];
  expires_at: number;
  granted_by_name: string;
  reason: string;
  created_at: number;
  active: boolean;
}
